import { type Concept, conceptApi } from '@mathflat/domain/Concept'
import {
  type Curriculum,
  curriculumApi,
  type CurriculumChapterTreesBySchoolParams,
  type CurriculumChapterTreesBySchoolTestParams,
  type CurriculumConceptTreeNode,
  type CurriculumCoreNodes,
  NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS,
  type TrieKey,
} from '@mathflat/domain/Curriculum'
import type {
  AllGradeSubject,
  GradeHigh,
  Revision,
  SchoolType,
  Semester,
} from '@mathflat/domain/SchoolSystem'
import { CurriculumTreeByChapter, MakeProblem, MakeProblemColl } from 'src/dto'
import CheckableMap from 'src/models/CheckableMap'

import { makeAdapter } from './Adapter'

class CurriculumController {
  adapter = {
    curriculumTreeByChapter: makeAdapter({
      from(curriculum: CurriculumConceptTreeNode[]) {
        return new CurriculumTreeByChapter(curriculum)
      },
    }),
    curriculumCore: {
      from(data: CurriculumCoreNodes) {
        const { revisions, schoolTypes, semesters: semester, subjects: subject } = data

        const activeRevisions = revisions.filter(({ status }) => status === 'ACTIVE')
        const activeSchools = schoolTypes.filter(({ status }) => status === 'ACTIVE')

        const map = new Map<
          SchoolType,
          {
            id: number
            trieKey: string
            grade: AllGradeSubject
            semester: Semester
            revision: Revision
            orderingNumber: number
          }[]
        >()

        const activeSemesters = semester
          .filter(({ status }) => status === 'ACTIVE')
          .sort((a, b) => a.orderingNumber - b.orderingNumber)
        const activeSubjects = subject
          .filter(
            ({ status, trieKey }) =>
              status === 'ACTIVE' &&
              !Object.values(NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS).includes(trieKey),
          )
          .sort((a, b) => a.orderingNumber - b.orderingNumber)

        const elementarySemesters = activeSemesters
          .filter(({ schoolType }) => schoolType === 'ELEMENTARY')
          .map(({ grade, semester, ...params }) => ({
            ...params,
            semester: semester as NonNullable<Semester>,
            grade: `${grade}` as AllGradeSubject,
          }))
        const middleSemesters = activeSemesters
          .filter(({ schoolType }) => schoolType === 'MIDDLE')
          .map(({ grade, semester, ...params }) => ({
            ...params,
            semester: semester as NonNullable<Semester>,
            grade: `${grade}` as AllGradeSubject,
          }))
        const highSubjectes = activeSubjects
          .filter(({ schoolType }) => schoolType === 'HIGH')
          .map(({ subject, ...params }) => ({
            ...params,
            grade: subject as GradeHigh,
            semester: null,
          }))

        map.set('ELEMENTARY', elementarySemesters)
        map.set('MIDDLE', middleSemesters)
        map.set('HIGH', highSubjectes)

        return {
          semesterSubjectMapBySchoolType: map,
          revisions: activeRevisions,
          schools: activeSchools,
        }
      },
    },

    curriculumIds: makeAdapter({
      from(response: Curriculum) {
        const { id, name, url } = response

        return new MakeProblem({
          conceptName: name,
          id,
          curriculumId: id || -1,
          conceptId: id,
          problemImageUrl: url || '',
          itemType: 'MIDDLE_CHAPTER_CONCEPT',
        })
      },
    }),
  }
  fetchCurriculumCore = () => curriculumApi.getCurriculumCore()

  /**
   * @deprecated
   */
  _fetchCurriculumsFromIds = async (
    curriculumIds: number[],
    type: 'LITTLE_CHAPTER' | 'CONCEPT',
  ) => {
    const data = await curriculumApi.getCurriculumsByIds(
      { idSet: curriculumIds, type },
      { mp_event_name: 'T35-11_학습지만들기2단계_소단원별개념조회_API' },
    )
    const list = data.filter(({ url }) => url).map(this.adapter.curriculumIds.from)
    return new MakeProblemColl(CheckableMap.fromArr(list, 'id'))
  }

  fetchCurriculumsFromIdsV2 = async (
    curriculumIds: number[],
    type: 'LITTLE_CHAPTER' | 'CONCEPT',
  ) => {
    const data = await curriculumApi.getCurriculumsByIds(
      { idSet: curriculumIds, type },
      { mp_event_name: 'T35-11_학습지만들기2단계_소단원별개념조회_API' },
    )
    return data.filter(({ url }) => url)
  }

  /* 커리큘럼 트리 */
  fetchCurriculumByTrieKey = (trieKey: TrieKey, options?: { arithmeticOnly?: boolean }) =>
    curriculumApi.getCurriculumChapterTreesByKey(
      { key: trieKey, arithmeticOnly: options?.arithmeticOnly },
      { mp_event_name: 'T35-11_학습지만들기2단계_소단원별개념조회_API' },
    )

  fetchCurriculumTreeByChapter = async (depth = 1) => {
    const nodes = await curriculumApi.getCurriculumConceptTreeNodes(depth, {
      mp_event_name: 'T42-07_단원별취약유형관리_단원트리_API',
    })
    return this.adapter.curriculumTreeByChapter.from(nodes)
  }

  fetchTopicAndSubTopicList = (conceptId: Concept['id']) => conceptApi.getConceptTopics(conceptId)

  fetchCurriculumsBySchoolTestIds = (params: CurriculumChapterTreesBySchoolTestParams) =>
    curriculumApi.getCurriculumChapterTreesBySchoolTest(params)

  fetchCurriculumsBySchool = (params: CurriculumChapterTreesBySchoolParams) =>
    curriculumApi.getCurriculumChapterTreesBySchool(params)
}

export const curriculumController = new CurriculumController()
